import type { EyeStyleOptions, EyeType, MouthType } from '../AppearanceDefinitions';
import { renderEyeStyle } from './eyes/EyeStyles';
import { MATCHED_FACING_SHIFT, renderMatchedEyes } from './eyes/MatchedEyes';
import type { HumanoidPose } from './AttachmentGeometry';

export interface FaceParts {
  eyes: string;
  nose: string;
  mouth: string;
}

const EYE_TYPES: readonly EyeType[] = ['round', 'happy', 'angry', 'winking', 'sleepy', 'stars'];
const MOUTH_TYPES: readonly MouthType[] = ['smile', 'o', 'neutral', 'grin', 'frown'];
const PUPIL = '#26313e';

/** Input color is only classified, never interpolated into SVG markup. */
function facialInk(color: string): string {
  const short = /^#([\da-f])([\da-f])([\da-f])$/i.exec(color);
  const hex = short ? `${short[1]}${short[1]}${short[2]}${short[2]}${short[3]}${short[3]}` : color.replace(/^#/, '');
  if (!/^[\da-f]{6}$/i.test(hex)) return '#303540';
  const n = parseInt(hex, 16);
  const luminance = 0.2126 * ((n >> 16) & 255) + 0.7152 * ((n >> 8) & 255) + 0.0722 * (n & 255);
  return luminance < 72 ? '#dce7ee' : '#303540';
}

function coordinate(value: number): number {
  return Number(value.toFixed(2));
}

/** Face options; `ghostEyes` is the cute ghost eye used by plan-12 art NPCs. */
export interface FaceOptions {
  ghostEyes?: boolean;
  /** A style from the plan-12 eye library; 'profile' keeps the near-big/far-small artwork. */
  eyeStyle?: EyeStyleOptions;
}

/**
 * Cute ghost eye: the same sockets as `openEye`, filled solid dark with a big
 * and a small catch-light — no sclera, no iris. Sleepy halves the height.
 */
function ghostEye(side: 'far' | 'near', ink: string, gazeX: number, gazeY: number, sleepy = false): string {
  const near = side === 'near';
  const x = coordinate((near ? 76 : 49) + gazeX * 0.6);
  const y = coordinate((near ? 42 : 44) + gazeY * 0.6);
  const rx = near ? 6.2 : 4.9;
  const ry = sleepy ? (near ? 3.8 : 3.1) : (near ? 7.8 : 6.2);
  const glint = near ? 2.2 : 1.7;
  const rim = ink === PUPIL || ink === '#303540' ? '' : ` stroke="${ink}" stroke-width="1"`;
  return `<g data-face-eye="${side}" data-face-ghost-eye="${side}">
    <ellipse data-face-socket="${side}" cx="${x}" cy="${y}" rx="${rx}" ry="${ry}" fill="${PUPIL}"${rim} />
    <circle cx="${coordinate(x + rx * 0.32)}" cy="${coordinate(y - ry * 0.38)}" r="${glint}" fill="#ffffff" />
    <circle cx="${coordinate(x - rx * 0.35)}" cy="${coordinate(y + ry * 0.35)}" r="${coordinate(glint * 0.45)}" fill="#ffffff" opacity="0.8" />
  </g>`;
}

function openEye(side: 'far' | 'near', ink: string, gazeX: number, gazeY: number, sleepy = false, ghost = false): string {
  if (ghost) return ghostEye(side, ink, gazeX, gazeY, sleepy);
  const near = side === 'near';
  const x = near ? 76 : 49;
  const y = near ? 42 : 44;
  const rx = near ? 6.2 : 4.4;
  const ry = sleepy ? (near ? 3.1 : 2.5) : (near ? 7 : 5.3);
  const radius = sleepy ? (near ? 1.9 : 1.5) : (near ? 3 : 2.2);
  const pupilX = coordinate(x + (near ? 1.5 : 1) + gazeX);
  const pupilY = coordinate(y + gazeY);
  return `<g data-face-eye="${side}">
    <ellipse data-face-socket="${side}" cx="${x}" cy="${y}" rx="${rx}" ry="${ry}" fill="${near ? '#fff9eb' : '#deded7'}" stroke="${ink}" stroke-width="${near ? 1.8 : 1.5}" />
    <circle data-face-pupil="${side}" cx="${pupilX}" cy="${pupilY}" r="${radius}" fill="${PUPIL}" />
    <circle cx="${coordinate(pupilX + 0.7)}" cy="${coordinate(pupilY - 0.9)}" r="${near ? 0.9 : 0.65}" fill="#ffffff" />
  </g>`;
}

function renderEyes(style: EyeType, ink: string, pose: HumanoidPose, ghost = false): string {
  const state = pose === 'death' || pose === 'blink' || pose === 'hurt' || pose === 'attack' || pose === 'cast'
    ? pose : 'open';
  const gazeX = pose === 'attack' ? 0.8 : pose === 'cast' ? 0.55 : 0;
  const gazeY = pose === 'cast' ? -0.5 : pose === 'attack' ? -0.2 : 0;
  const stroke = `fill="none" stroke="${ink}" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round"`;
  let content: string;

  if (pose === 'death') {
    content = `<path ${stroke} d="M44,40 L53,48 M53,40 L44,48 M70,36 L82,48 M82,36 L70,48" />`;
  } else if (pose === 'blink') {
    content = `<path ${stroke} d="M42,44 Q49,49 55,44 M67,42 Q76,49 85,41" />`;
  } else if (pose === 'hurt') {
    content = `<path ${stroke} d="M43,39 L52,44 L43,48 M83,36 L72,42 L84,47" />`;
  } else {
    switch (style) {
    case 'happy':
      content = `<path ${stroke} d="M42,45 Q49,35 56,43 M67,43 Q77,29 86,40" />`;
      break;
    case 'winking':
      content = openEye('far', ink, gazeX, gazeY, false, ghost)
        + `<path ${stroke} d="M67,43 Q76,34 86,41" />`;
      break;
    case 'stars':
      content = `<g data-face-star-eyes="true" fill="#ffd36a" stroke="${ink}" stroke-width="1.1" stroke-linejoin="round" transform="translate(${gazeX} ${gazeY})">
        <path d="M49,37 L51,42 L57,43 L52,46 L53,52 L49,49 L44,52 L45,46 L41,43 L47,42 Z" />
        <path d="M76,33 L79,39 L86,40 L81,45 L82,52 L76,49 L70,53 L71,45 L66,41 L73,40 Z" />
      </g>`;
      break;
    case 'sleepy':
      content = openEye('far', ink, gazeX, 0, true, ghost) + openEye('near', ink, gazeX, 0, true, ghost)
        + (ghost ? '' : `<path ${stroke} d="M44,41 L55,42 M69,38 L85,39" />`);
      break;
    case 'angry':
      content = openEye('far', ink, gazeX, gazeY, false, ghost) + openEye('near', ink, gazeX, gazeY, false, ghost)
        + `<path ${stroke} d="M42,35 L55,39 M85,31 L69,37" />`;
      break;
    case 'round':
      content = openEye('far', ink, gazeX, gazeY, false, ghost) + openEye('near', ink, gazeX, gazeY, false, ghost);
      break;
    }
    if (pose === 'attack' && (style === 'round' || style === 'winking')) {
      content += `<path data-face-focus="attack" ${stroke} d="M44,35 L54,37 M70,32 L83,30" />`;
    } else if (pose === 'cast' && style === 'round') {
      content += `<path data-face-focus="cast" ${stroke} d="M44,34 Q50,31 55,34 M70,30 Q77,25 84,29" />`;
    }
  }
  return `<g data-face-style="${style}" data-face-state="${state}" data-face-gaze="right">${content}</g>`;
}

function renderMouth(style: MouthType, ink: string): string {
  const stroke = `fill="none" stroke="${ink}" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round"`;
  let content: string;
  switch (style) {
  case 'o':
    content = `<ellipse cx="70" cy="64" rx="4.2" ry="5.5" fill="${ink}" />`;
    break;
  case 'neutral':
    content = `<path ${stroke} d="M59,65 L78,63" />`;
    break;
  case 'grin':
    content = `<path d="M55,59 Q69,73 82,58 Z" fill="${PUPIL}" stroke="${ink}" stroke-width="1.2" />
      <path d="M59,60 Q70,63 78,59" fill="none" stroke="#fff9eb" stroke-width="2.1" stroke-linecap="round" />`;
    break;
  case 'frown':
    content = `<path ${stroke} d="M56,70 Q69,56 82,67" />`;
    break;
  case 'smile':
    content = `<path ${stroke} d="M56,60 Q68,71 82,58" />`;
    break;
  }
  return `<g data-face-mouth="${style}">${content}</g>`;
}

/**
 * Art in the existing right-facing face coordinates. The caller retains the
 * shape-offset wrapper, beard ordering and whole-sprite left-facing mirror.
 * Poses are sampled inputs; this leaf owns no animation clock or target state.
 */
export function renderFaceParts(
  color: string,
  eyes: EyeType,
  mouth: MouthType,
  pose: HumanoidPose = 'idle',
  options: FaceOptions = {},
): FaceParts {
  const ink = facialInk(color);
  const eyeStyle = EYE_TYPES.includes(eyes) ? eyes : 'round';
  const mouthStyle = MOUTH_TYPES.includes(mouth) ? mouth : 'smile';
  const library = options.eyeStyle;
  const matched = library?.style === 'matched';
  const mouthArt = renderMouth(mouthStyle, ink);
  let eyesContent: string;
  if (matched) eyesContent = renderMatchedEyes(eyeStyle, ink, pose, library, !!options.ghostEyes);
  else if (library && library.style !== 'profile') eyesContent = renderEyeStyle(library, ink, pose);
  else eyesContent = renderEyes(eyeStyle, ink, pose, !!options.ghostEyes && !library);
  return {
    eyes: eyesContent,
    nose: `<path data-face-nose="right" d="M86,47 Q94,51 86,55" fill="none" stroke="${ink}" stroke-width="1.8" stroke-linecap="round" opacity="0.82" />`,
    // Matched eyes carry facing by placement: the mouth moves with them.
    mouth: matched ? `<g data-face-facing-shift="${MATCHED_FACING_SHIFT}" transform="translate(${MATCHED_FACING_SHIFT} 0)">${mouthArt}</g>` : mouthArt,
  };
}
